import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Line } from 'react-chartjs-2'
import { defaultTheme } from '@dataplace.ai/ui-components/themes/defaultTheme'
import { reduceOpacity } from '@dataplace.ai/functions/utils/styles'
import NumberFormat from 'react-number-format'
import { Tooltip } from '@dataplace.ai/ui-components/atoms'
import useCheckIfPrinting from 'apps/placeme/src/customHooks/useCheckIfPrinting'
import { DataBox, DataRow, DataTitle, IndicatorBox, StyledChartArrow, UniqueTraffic } from './AverageTraffic.styles'
import { StyledWrapper, TitleWrapper } from '../SharedOverview.styles'
import CatchmentInfoComponent from '../components/CatchmentInfoComponent'
import { NoDataGuard } from '../components/NoDataGuard'
import { NO_OVERVIEW_DATA } from '../constants'

interface AverageTrafficProps {
  year: number
  catchment: {
    mode: string
    length: number
  }
  percentageDiffDailyAverage: number
  dailyAverageUniqueTraffic: number
  monthly: {
    month: number
    avgDaily: number
  }[]
}

export type AverageTrafficData = AverageTrafficProps | typeof NO_OVERVIEW_DATA
const TILE_TITLE_KEY = 'placeme.category.traffic'

function AVERAGE_TRAFFIC({
  year, catchment, dailyAverageUniqueTraffic, percentageDiffDailyAverage, monthly,
}: AverageTrafficProps): JSX.Element {
  const { t } = useTranslation()
  const { isPrinting } = useCheckIfPrinting()

  const diffColor = percentageDiffDailyAverage > 0
    ? defaultTheme.palette.results.green.light
    : percentageDiffDailyAverage < 0
      ? defaultTheme.palette.results.red.light
      : defaultTheme.palette.dark.normal

  const percentageDiffText = useMemo(() => {
    const sign = percentageDiffDailyAverage >= 0 ? '+' : ''
    return `${sign}${percentageDiffDailyAverage}%`
  }, [percentageDiffDailyAverage])

  return (
    <StyledWrapper>
      <TitleWrapper>
        {t(TILE_TITLE_KEY)}
        <CatchmentInfoComponent {...catchment} />
      </TitleWrapper>
      <DataRow>
        <DataTitle>
          {t('placeme.overview.average_traffic.avg_traffic_in')}
          {' '}
          {year}
        </DataTitle>
        <DataBox>
          <UniqueTraffic>
            <NumberFormat
              displayType='text'
              thousandSeparator=' '
              value={dailyAverageUniqueTraffic}
            />
          </UniqueTraffic>
          <Tooltip
            content={t('placeme.analyse.overview.average_traffic.percentage_diff_daily_average')}
            position='top center'
          >
            <IndicatorBox diff={percentageDiffDailyAverage}>
              {percentageDiffText}
              <StyledChartArrow diff={percentageDiffDailyAverage} />
            </IndicatorBox>
          </Tooltip>
        </DataBox>
      </DataRow>
      <Line
        data={{
          labels: monthly.map(monthObject => monthObject.month),
          datasets: [{
            data: monthly.map(monthObject => monthObject.avgDaily),
            backgroundColor: reduceOpacity(diffColor, 0.2),
            borderColor: diffColor,
          }],
        }}
        height={isPrinting ? 100 : 200}
        options={{
          legend: {
            display: false,
          },
          elements: {
            point: {
              radius: 0, // remove the dots on each data point
            },
          },
          scales: {
            xAxes: [{
              gridLines: {
                display: false, // remove vertical grid lines
                drawBorder: false,
              },
              ticks: {
                display: true, // you can set to false if you want to hide x labels too
              },
            }],
            yAxes: [{
              position: 'right', // move y-axis labels to the right
              gridLines: {
                display: true, // remove horizontal grid lines
                drawBorder: false,
              },
              ticks: {
                display: true, // set to false if you want no y-axis labels
              },
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              afterDataLimits(scale: any) {
                const range = scale.max - scale.min
                const padding = range * 0.10

                scale.max += padding
                scale.min -= padding
              },
            }],
          },
          plugins: {
            datalabels: {
              display: false, // disables values shown on the line
            },
          },
        }}
      />
    </StyledWrapper>
  )
}

export function AverageTraffic({ data }: {data: AverageTrafficData}): JSX.Element {
  return (
    <NoDataGuard
      component={AVERAGE_TRAFFIC}
      data={data}
      title={TILE_TITLE_KEY}
    />
  )
}
